import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import db from '../services/db';
import i18n from 'i18next';

// 设置接口
interface SettingsState {
  darkMode: boolean;
  showMilliseconds: boolean;
  notificationSound: string;
  notificationsEnabled: boolean;
  language: string;
  status: 'idle' | 'loading' | 'succeeded' | 'failed';
  error: string | null;
}

// 初始状态
const initialState: SettingsState = {
  darkMode: true, // 默认使用暗色模式
  showMilliseconds: false, // 默认不显示毫秒
  notificationSound: 'default', // 默认通知声音
  notificationsEnabled: true, // 默认启用通知
  language: 'en', // 默认使用英文
  status: 'idle',
  error: null,
};

// 异步操作：获取设置
export const fetchSettings = createAsyncThunk(
  'settings/fetch',
  async () => {
    return await db.getSettings();
  }
);

// 异步操作：更新设置
export const updateSettings = createAsyncThunk(
  'settings/update',
  async (changes: Partial<Omit<SettingsState, 'status' | 'error'>>) => {
    await db.updateSettings(changes);
    return changes;
  }
);

// 创建设置Slice
const settingsSlice = createSlice({
  name: 'settings',
  initialState,
  reducers: {
    // 切换暗色模式
    toggleDarkMode: (state) => {
      state.darkMode = !state.darkMode;
    },
    
    // 切换显示毫秒
    toggleShowMilliseconds: (state) => {
      state.showMilliseconds = !state.showMilliseconds;
    },
    
    // 设置通知声音
    setNotificationSound: (state, action: PayloadAction<string>) => {
      state.notificationSound = action.payload;
    },
    
    // 切换通知
    toggleNotifications: (state) => {
      state.notificationsEnabled = !state.notificationsEnabled;
    },
    
    // 设置语言
    setLanguage: (state, action: PayloadAction<string>) => {
      state.language = action.payload;
      i18n.changeLanguage(action.payload); // 直接更改i18n当前语言
    },
  },
  extraReducers: (builder) => {
    builder
      // 获取设置 - pending
      .addCase(fetchSettings.pending, (state) => {
        state.status = 'loading';
      })
      // 获取设置 - fulfilled
      .addCase(fetchSettings.fulfilled, (state, action) => {
        state.status = 'succeeded';
        // 更新状态
        state.darkMode = action.payload.darkMode;
        state.showMilliseconds = action.payload.showMilliseconds;
        state.notificationSound = action.payload.notificationSound;
        state.notificationsEnabled = action.payload.notificationsEnabled;
        
        // 更新语言设置（如果有的话）
        if (action.payload.language) {
          state.language = action.payload.language;
          // 确保语言在支持的范围内
          const supportedLanguages = ['en', 'zh', 'es', 'fr', 'ja'];
          if (supportedLanguages.includes(action.payload.language)) {
            i18n.changeLanguage(action.payload.language);
          } else {
            // 如果语言不支持，设置为默认语言
            i18n.changeLanguage('en');
            state.language = 'en';
          }
        }
      })
      // 获取设置 - rejected
      .addCase(fetchSettings.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message || '获取设置失败';
      })
      // 更新设置 - pending
      .addCase(updateSettings.pending, (state) => {
        state.status = 'loading';
      })
      // 更新设置 - fulfilled
      .addCase(updateSettings.fulfilled, (state, action) => {
        state.status = 'succeeded';
        // 更新状态
        Object.assign(state, action.payload);
      })
      // 更新设置 - rejected
      .addCase(updateSettings.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message || '更新设置失败';
      });
  },
});

 // 导出action creators
export const { 
  toggleDarkMode, 
  toggleShowMilliseconds, 
  setNotificationSound, 
  toggleNotifications,
  setLanguage
} = settingsSlice.actions;

export default settingsSlice.reducer;